import { useEffect, useState } from "react";
import PortalLayout from "@/components/PortalLayout";
import { api, apiErr } from "@/lib/api";
import { usePortalAuth } from "@/context/PortalAuth";
import { toast } from "sonner";
import { Plus } from "lucide-react";

export default function PortalComplaints() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ subject: "", description: "", unit_id: "" });
  const [open, setOpen] = useState(false);
  const { me } = usePortalAuth();
  const units = [...(me?.owned_units || []), ...(me?.occupied_units || [])];

  const load = () => api.get("/portal/complaints").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/portal/complaints", {
        subject: form.subject, description: form.description,
        unit_id: form.unit_id || units[0]?.id,
      });
      toast.success("Complaint raised. Committee has been notified.");
      setForm({ subject: "", description: "", unit_id: "" });
      setOpen(false);
      load();
    } catch (err) { toast.error(apiErr(err)); }
  };

  return (
    <PortalLayout
      subtitle="Complaints"
      title="Your complaints"
      actions={
        <button onClick={() => setOpen(!open)} data-testid="new-complaint"
          className="inline-flex items-center gap-2 rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-4 py-2 text-sm">
          <Plus size={14}/> New complaint
        </button>
      }
    >
      {open && (
        <form onSubmit={submit} className="mb-6 bg-white border border-stone-200 rounded-2xl p-6 space-y-3" data-testid="complaint-form">
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Subject</label>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} required
              data-testid="c-subject"
              className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base outline-none focus:border-terracotta"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Description</label>
            <textarea rows="4" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required
              data-testid="c-desc"
              className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base outline-none focus:border-terracotta"/>
          </div>
          {units.length > 1 && (
            <div>
              <label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Unit</label>
              <select value={form.unit_id} onChange={(e) => setForm({ ...form, unit_id: e.target.value })}
                className="mt-2 w-full rounded-xl border border-stone-300 bg-white px-4 py-3 text-base outline-none focus:border-terracotta">
                <option value="">— select unit —</option>
                {units.map((u) => <option key={u.id} value={u.id}>{u.building}{u.wing ? "/" + u.wing : ""}/{u.unit_no}</option>)}
              </select>
            </div>
          )}
          <div className="flex justify-end"><button type="submit" data-testid="c-submit" className="rounded-full bg-terracotta hover:bg-terracotta-hover text-white px-6 py-2.5 text-sm">Submit</button></div>
        </form>
      )}

      <div className="space-y-3">
        {rows.map((c) => (
          <div key={c.id} className="bg-white border border-stone-200 rounded-2xl p-5" data-testid={`c-${c.id}`}>
            <div className="flex items-center justify-between">
              <div className="editorial text-lg">{c.subject}</div>
              <Status s={c.status}/>
            </div>
            <p className="text-sm text-stone-600 mt-2 whitespace-pre-wrap">{c.description}</p>
            <div className="text-xs text-stone-500 mt-2">{c.created_at?.slice(0, 16).replace("T", " ")}</div>
            {c.remarks && <div className="mt-2 text-sm text-stone-700 bg-sand-100 border border-stone-200 rounded-lg p-3">Committee: {c.remarks}</div>}
          </div>
        ))}
        {rows.length === 0 && <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center text-stone-500">No complaints raised yet.</div>}
      </div>
    </PortalLayout>
  );
}
function Status({ s }) {
  const m = { Open: "bg-amber-100 text-amber-800", "In Progress": "bg-blue-100 text-blue-800", Resolved: "bg-emerald-100 text-emerald-800" };
  return <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${m[s] || "bg-stone-100"}`}>{s}</span>;
}
